import {useCallback, useMemo, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, Modal, Select, Tag} from 'antd';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {useSendUserNotification} from 'components/sendUserNotification';
import {SEND_KYC_NOTIFICATION} from 'graphql/mutations/users';
import {
  CUSTOM_REASON,
  KYC_NOTIFICATION_REASONS,
  RESET_REASONS,
  readChannelResults,
  reasonLabel,
} from 'pages/adminUser/userDetails/updateKyc/kycNotification';
import styles from './kycDashboard.module.scss';

/**
 * Notify, from a row of either KYC queue.
 *
 * The same thing the user's KYC tab offers, reached from the queue instead: an
 * admin picks one of the stored reasons and the service composes the wording,
 * the language and the channels itself. Only "Other" has no stored template,
 * and that one hands off to the ordinary notification composer — the wallet,
 * order and swap tables' modal — where the message is written and reviewed
 * before it goes.
 *
 * Which is why Notify no longer opens that composer directly. Almost every
 * KYC notice is one of thirteen fixed ones, and making an admin write those by
 * hand invites thirteen slightly different wordings for the same situation.
 *
 * The reason list, the reset warning and the channel readout come from the KYC
 * tab's own module, so the two screens cannot drift apart.
 */

const languageOf = user => (user?.language === 'FR' ? 'French' : 'English');

const fullNameOf = user =>
  [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'This account';

const KycNotifyModal = ({user, onClose, onCompose, canCompose}) => {
  const [reason, setReason] = useState(undefined);
  const [result, setResult] = useState(null);
  const [sendKycNotification, {loading}] = useMutation(SEND_KYC_NOTIFICATION, {
    errorPolicy: 'all',
  });

  const isCustomReason = reason === CUSTOM_REASON;
  const channels = useMemo(
    () => (result ? readChannelResults(result.channels) : []),
    [result],
  );

  /**
   * Never retried automatically: a channel may already have been dispatched
   * before the failure, so a silent second attempt could send the same notice
   * twice. The reason stays selected so an admin can resend deliberately.
   */
  const executeSend = async () => {
    setResult(null);

    try {
      const {data, errors} = await sendKycNotification({
        // Exactly the two fields the input takes — no copy, language or
        // channel list; the service resolves all of that from its templates.
        variables: {input: {userId: user.id, reason}},
      });

      if (errors?.length) return toast.error(errors[0].message);

      const dispatch = data?.sendKycNotificationToUser;
      // Read __typename before treating anything as a success.
      if (dispatch?.__typename !== 'ResponseWithKycNotificationDispatch') {
        return toast.error(
          dispatch?.message ?? 'Could not send the notification',
        );
      }

      setResult(dispatch);
      toast.success(dispatch.message ?? 'KYC notification sent');
    } catch (error) {
      toast.error(error?.message ?? 'Could not send the notification');
    }
  };

  /**
   * One click reaches the user on three channels at once and there is no
   * recall, so the send is confirmed against the recipient and the wording —
   * the two things worth checking, and the two a misclicked row gets wrong.
   */
  const handleSend = () => {
    if (!reason || !user?.id) return;

    Modal.confirm({
      title: 'Send this KYC notification?',
      content: (
        <span>
          {reasonLabel(reason)} goes to <strong>{user?.email}</strong> by push,
          email and in-app notice, in {languageOf(user)}. It does not change
          their KYC status, and it cannot be recalled.
        </span>
      ),
      okText: 'Send notification',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => executeSend(),
    });
  };

  return (
    <div className={styles.notifyModal}>
      <div className={styles.notifyHeader}>
        <h1>Notify this user</h1>
        <p>
          The wording, the language and the channels come from the stored
          template for the reason you pick. Sending changes nothing about their
          KYC status.
        </p>
      </div>

      <div className={styles.notifyField}>
        <span className={styles.notifyLabel}>Recipient</span>
        <span className={styles.notifyValue}>{user?.email ?? '—'}</span>
        <span className={styles.notifyHint}>
          {fullNameOf(user)} reads {languageOf(user)}, so the {languageOf(user)}{' '}
          wording is sent.
        </span>
      </div>

      <div className={styles.notifyField}>
        <label className={styles.notifyLabel} htmlFor="kyc-notify-reason">
          Reason
        </label>
        <Select
          id="kyc-notify-reason"
          placeholder="Pick a reason"
          value={reason}
          onChange={next => {
            setReason(next);
            // Last run's channel results belong to that run.
            setResult(null);
          }}
          options={KYC_NOTIFICATION_REASONS}
          showSearch
          optionFilterProp="label"
          allowClear
        />
        <span className={styles.notifyHint}>
          {isCustomReason
            ? 'You write this one yourself.'
            : 'Picks the wording the user receives.'}
        </span>
      </div>

      {RESET_REASONS.includes(reason) && (
        <div className={styles.notifyWarning}>
          This wording tells the user their verification has already been reset.
          Carry out the reset first.
        </div>
      )}

      {isCustomReason && (
        <div className={styles.notifyInfo}>
          There is no stored wording for this one. Composing opens the usual
          notification editor, where you write the message, translate it and
          check it before sending.
        </div>
      )}

      {result && (
        <ul className={styles.notifyChannels}>
          {channels.map(channel => (
            <li key={channel.key}>
              <span>{channel.label}</span>
              <Tag color={channel.tone}>{channel.statusLabel}</Tag>
              {channel.detail && (
                <span className={styles.notifyHint}>{channel.detail}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className={styles.notifyActions}>
        <Button onClick={onClose}>{result ? 'Done' : 'Cancel'}</Button>
        {isCustomReason ? (
          <Button
            type="primary"
            className={styles.notifySend}
            disabled={!canCompose}
            title={
              canCompose
                ? undefined
                : 'You need notification create permission to compose one'
            }
            onClick={onCompose}>
            Compose notification
          </Button>
        ) : (
          <Button
            type="primary"
            className={styles.notifySend}
            disabled={!reason || loading}
            loading={loading}
            onClick={handleSend}>
            Send KYC notification
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Wires the row action into a queue tab.
 *
 *   const {canNotify, openKycNotify, kycNotifyModals} = useKycNotify();
 *   // …in the row: onClick={() => openKycNotify(record.user)}
 *   // …once, next to the table: {kycNotifyModals}
 *
 * Both overlays are returned together: picking "Other" closes the reason modal
 * and opens the composer in its place, and the caller should not have to know
 * that there are two.
 */
export const useKycNotify = () => {
  const [target, setTarget] = useState(null);
  const {canSendNotification, openSendNotification, sendNotificationModal} =
    useSendUserNotification();

  const openKycNotify = useCallback(user => setTarget(user || null), []);
  const closeKycNotify = useCallback(() => setTarget(null), []);

  const openComposer = useCallback(() => {
    const user = target;
    setTarget(null);
    openSendNotification(
      user,
      {label: 'KYC', reference: user?.email},
      {
        // Seeded so only the wording is left to write: the notice goes out in
        // the account's own language, on the channels every KYC notice uses.
        baseLanguage: user?.language === 'FR' ? 'FR' : 'EN',
        category: ['PUSH', 'IN_APP'],
        sendMode: 'NOW',
      },
    );
  }, [target, openSendNotification]);

  const kycNotifyModals = (
    <>
      <DBModal
        isOpen={Boolean(target)}
        handleClose={closeKycNotify}
        width={560}
        destroyOnClose
        content={
          target ? (
            <KycNotifyModal
              user={target}
              onClose={closeKycNotify}
              onCompose={openComposer}
              canCompose={canSendNotification}
            />
          ) : null
        }
      />
      {sendNotificationModal}
    </>
  );

  return {canNotify: canSendNotification, openKycNotify, kycNotifyModals};
};
